import * as React from "react";
import { Typography, Box } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import AddIcon from "@mui/icons-material/Add";
import { searchEvents } from "../../app/store/actions/eventActions";
import {
  PlatformPage,
  PlatformPageHeader,
  PlatformEventCard,
  PlatformStatsGrid,
  PlatformStatCard,
  PlatformCard,
  PlatformAnalyticBar,
  PlatformButton,
  PlatformEmptyState,
  PlatformLoadingBlock,
} from "../../app/components/platform";
import { platformColors, platformFonts } from "../../app/components/tickitPlatformTheme";

export const AdminHome = ({ events, searchEvents, auth }) => {
  const navigate = useNavigate();
  const role = auth?.token?.split(" ")[0] || "Admin";

  React.useEffect(() => {
    searchEvents({
      search: "",
      page: 1,
      limit: 48,
      minDate: moment().format("yyyy-MM-DD"),
      maxDate: moment().add(1, "year").format("yyyy-MM-DD"),
    });
  }, [searchEvents]);

  const docs = events?.results?.docs || [];
  const upcoming = docs.filter((e) => e?.date && moment().isBefore(moment(e.date)));
  const corporate = upcoming.filter((e) => e?.corporate);
  const next7 = upcoming.filter(
    (e) => moment(e.date).diff(moment(), "days") <= 7
  );

  return (
    <PlatformPage>
      <PlatformPageHeader
        title="Platform overview"
        subtitle="All partners · Events · Live operations"
        actions={
          <>
            {role === "Super" && (
              <PlatformButton
                variant="ghost"
                size="small"
                onClick={() => navigate("/Super/Partner/search")}
              >
                Partners
              </PlatformButton>
            )}
            <PlatformButton
              variant="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate("/events/create")}
            >
              New event
            </PlatformButton>
          </>
        }
      />

      <PlatformStatsGrid>
        <PlatformStatCard
          label="Upcoming events"
          value={upcoming.length}
          delta={`${next7.length} in next 7 days`}
          deltaTone="up"
          icon={<EventOutlinedIcon />}
          iconBg={platformColors.brandPale}
          iconColor={platformColors.brand}
        />
        <PlatformStatCard
          label="RSVP / corporate"
          value={corporate.length}
          delta="Invitation-driven events"
          deltaTone="up"
          icon={<MailOutlineIcon />}
          iconBg="#D1FAE5"
          iconColor="#065F46"
        />
        <PlatformStatCard
          label="Ticketed upcoming"
          value={upcoming.length - corporate.length}
          delta="Public & paid flows"
          icon={<InboxOutlinedIcon />}
          iconBg="#DBEAFE"
          iconColor="#1E40AF"
        />
        <PlatformStatCard
          label="Admin tools"
          value={role === "Super" ? "Full" : "Standard"}
          delta="WhatsApp · Requests · Scanner"
          icon={<StorefrontOutlinedIcon />}
          iconBg="#FEF3C7"
          iconColor="#92400E"
        />
      </PlatformStatsGrid>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <PlatformCard title="Event mix">
          <PlatformAnalyticBar
            label="Corporate / RSVP"
            percent={upcoming.length ? (corporate.length / upcoming.length) * 100 : 0}
            value={`${corporate.length}`}
            color={platformColors.brand}
          />
          <PlatformAnalyticBar
            label="Standard ticketing"
            percent={
              upcoming.length
                ? ((upcoming.length - corporate.length) / upcoming.length) * 100
                : 0
            }
            value={`${upcoming.length - corporate.length}`}
            color={platformColors.info}
          />
          <PlatformAnalyticBar
            label="Next 7 days"
            percent={upcoming.length ? (next7.length / upcoming.length) * 100 : 0}
            value={`${next7.length}`}
            color={platformColors.accent2}
          />
        </PlatformCard>
        <PlatformCard title="Quick links">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <PlatformButton variant="ghost" onClick={() => navigate("/events")}>
              Browse all events
            </PlatformButton>
            <PlatformButton variant="ghost" onClick={() => navigate("/Admins/TicketRequests")}>
              Ticket requests
            </PlatformButton>
            <PlatformButton variant="ghost" onClick={() => navigate("/Admin/whatsappCampaigns")}>
              WhatsApp campaigns
            </PlatformButton>
            <PlatformButton variant="ghost" onClick={() => navigate("/Admin/whatsappConversations")}>
              WhatsApp conversations
            </PlatformButton>
          </Box>
        </PlatformCard>
      </Box>

      <Typography
        sx={{
          fontFamily: platformFonts.display,
          fontWeight: 700,
          fontSize: "0.9375rem",
          color: 'var(--platform-text)',
          mb: 1.5,
        }}
      >
        Upcoming events
      </Typography>

      {events?.isLoading ? (
        <PlatformLoadingBlock label="Loading events…" />
      ) : upcoming.length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 1.75,
          }}
        >
          {upcoming.map((event) => (
            <PlatformEventCard key={event._id} event={event} role={role} />
          ))}
        </Box>
      ) : (
        <PlatformEmptyState
          title="No upcoming events"
          description="No events scheduled in the next year. Create one to get started."
          actionLabel="New event"
          onAction={() => navigate("/events/create")}
        />
      )}
    </PlatformPage>
  );
};

const mapStateToProps = (state) => ({
  events: state?.events,
  auth: state?.auth,
});

const mapDispatchToProps = { searchEvents };

export default connect(mapStateToProps, mapDispatchToProps)(AdminHome);
